import { t } from "i18next";
import { setSalesSupports } from "../store";
import { isInclude, MODE_OF_SALE_LABEL } from "./modeOfSaleCheckers";
import { diff } from "deep-object-diff";

export async function GetSalesModesTypesAndTagsV2(
    franchiseID: any,
    shopId: any,
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>
): Promise<SalesConfig[]> {
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";

    const query = new URLSearchParams({
        franchiseId: franchiseID,
        shopId: shopId,
    });

    const url = `${
        process.env.REACT_APP_API_V2_URL
    }/settings/setup?${query.toString()}`;

    try {
        const data = await fetchGlobal<any>(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Accept-Language": i18nextLng,
            },
        });

        if (data.error) {
            throw new Error(data.error);
        }

        if (Object.keys(data.data.settings).length === 0) {
            return [
                {
                    tags: [],
                    saleMode: [],
                    ListSaleSupport: [],
                    languages: [],
                    saleSupport: [],
                    currencies: [],
                },
            ];
        }

        const shopIds = parseShopIds(shopId);

        return shopIds.map((el) => buildShopConfig(el, data.data, t));
    } catch (error) {
        console.error("Error fetching sales modes/types/tags:", error);
        throw error;
    }
}

export async function fetchSupport(
    userID: any,
    franchiseID: any,
    i18nextLng: any,
    t: any,
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>
): Promise<void> {
    const url = `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport?userId=${userID}&franchiseId=${franchiseID}&type=activated`;

    try {
        const result = await fetchGlobal<any>(url, {
            headers: {
                "Accept-Language": i18nextLng,
            },
        });

        if (!Array.isArray(result.data)) {
            throw new Error("Unexpected response format: data is not an array");
        }

        const supports = result.data.map((el: any) => ({
            id: el.shopId,
            salesSupport: Array.isArray(el.subRows)
                ? el.subRows.map((s: any) => s[t("Sale support")])
                : [],
        }));

        setSalesSupports(supports);
    } catch (err) {
        console.error("Error fetching supports:", err);
    }
}

export function checkRequiredFields(
    formState: any,
    salesSupport: any,
    i18nextLng: string,
    setInputSaleMode: any,
    setSelectSales: any,
    setSelectName: any
) {
    if (formState.saleMode === "") {
        setInputSaleMode(true);
    }
    if (salesSupport.length === 0) {
        setSelectSales(true);
    }
    if (formState.advancedDisplayName.languages[i18nextLng] === "") {
        setSelectName(true);
    }
}

export function isFormUnchanged(
    formState: any,
    dataSale: any,
    tagsData: any,
    salesSupport: any
): boolean {
    return (
        dataSale[MODE_OF_SALE_LABEL] === formState.saleMode &&
        dataSale.advancedDisplayName === formState.advancedDisplayName &&
        dataSale.tag.every((el: any) => isInclude(el, tagsData)) &&
        tagsData.every((el: any) => isInclude(el, dataSale.tag)) &&
        dataSale.support_vente.every((el: any) =>
            isInclude(el, salesSupport)
        ) &&
        salesSupport.every((el: any) =>
            isInclude(el, dataSale.support_vente)
        ) &&
        Object.keys(
            diff(dataSale.advancedDisplayName, formState.advancedDisplayName)
        ).length === 0
    );
}

export function isFormValid(
    formState: any,
    salesSupport: any,
    isNameExist: any,
    i18nextLng: string
): boolean {
    return (
        formState.saleMode !== "" &&
        !isNameExist &&
        formState.advancedDisplayName.languages[i18nextLng] !== "" &&
        salesSupport.length !== 0
    );
}
function parseShopIds(shopId: string | string[]): string[] {
    if (Array.isArray(shopId)) return shopId;

    const decoded = fullyDecodeURI(String(shopId));

    try {
        const parsed = JSON.parse(decoded);
        return Array.isArray(parsed) ? parsed : [decoded];
    } catch {
        return [decoded];
    }
}

function fullyDecodeURI(value: string): string {
    let decoded = value;
    let prev = "";

    while (decoded !== prev && decoded.includes("%")) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch {
            break;
        }
    }

    return decoded.replace(/^"|"$/g, "");
}
function buildShopConfig(
    el: string,
    data: ApiResponse["data"],
    t: (key: string) => string
): SalesConfig {
    const shopSettings = data.settings[el];

    const result = {
        tags: shopSettings?.tag ?? [],
        saleMode: shopSettings?.mode_of_sale_list ?? [],
        ListSaleSupport: shopSettings?.setting_sale_support ?? [],
        languages: shopSettings?.setting_language ?? [],
        saleSupport: shopSettings?.saleSupport ?? [],
        listModeOfInformation: shopSettings?.mode_of_information_list ?? [],
        modeOfSales: shopSettings?.setting_sale_mode ?? [],
        shopId: el,
        currencies: data.currencies ?? [],
    };

    return result;
}

interface ApiResponse {
    data: {
        settings: Record<string, ShopSettings>;
        currencies?: string[];
    };
}

interface SalesConfig {
    shopId?: string;
    tags: string[];
    saleMode: string[];
    ListSaleSupport: string[];
    languages: string[];
    saleSupport: string[];
    listModeOfInformation?: string[];
    modeOfSales?: string[];
    currencies: string[];
}

interface ShopSettings {
    tag?: string[];
    mode_of_sale_list?: string[];
    setting_sale_support?: string[];
    setting_language?: string[];
    saleSupport?: string[];
    mode_of_information_list?: string[];
    setting_sale_mode?: string[];
}
